import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Settings, Calendar, Clock } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { appointmentConfigAPI, centersAPI } from '../../utils/api';

const AdminAppointmentConfig = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  
  const [formData, setFormData] = useState({
    center_id: '',
    max_appointments_per_slot: 5,
    max_appointments_per_day: 50,
    advance_booking_days: 30,
    min_booking_notice_hours: 24,
    allow_weekend_booking: false,
    blocked_dates: []
  });
  
  const [blockedDateInput, setBlockedDateInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configsRes, centersRes] = await Promise.all([
        appointmentConfigAPI.getAll(),
        centersAPI.getAll(true)
      ]);
      setConfigs(configsRes.data);
      setCenters(centersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConfig) {
        const { center_id, ...updateData } = formData;
        await appointmentConfigAPI.update(editingConfig.id, updateData);
        toast.success('Configuration updated');
      } else {
        await appointmentConfigAPI.create(formData);
        toast.success('Configuration created');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      center_id: config.center_id,
      max_appointments_per_slot: config.max_appointments_per_slot,
      max_appointments_per_day: config.max_appointments_per_day,
      advance_booking_days: config.advance_booking_days,
      min_booking_notice_hours: config.min_booking_notice_hours,
      allow_weekend_booking: config.allow_weekend_booking,
      blocked_dates: config.blocked_dates || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (configId) => {
    if (window.confirm('Delete this configuration?')) {
      try {
        await appointmentConfigAPI.delete(configId);
        toast.success('Configuration deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetForm = () => {
    setEditingConfig(null);
    setFormData({
      center_id: '',
      max_appointments_per_slot: 5,
      max_appointments_per_day: 50,
      advance_booking_days: 30,
      min_booking_notice_hours: 24,
      allow_weekend_booking: false,
      blocked_dates: []
    });
    setBlockedDateInput('');
  };

  const addBlockedDate = () => {
    if (blockedDateInput && !formData.blocked_dates.includes(blockedDateInput)) {
      setFormData(prev => ({
        ...prev,
        blocked_dates: [...prev.blocked_dates, blockedDateInput].sort()
      }));
      setBlockedDateInput('');
    }
  };

  const removeBlockedDate = (date) => {
    setFormData(prev => ({
      ...prev,
      blocked_dates: prev.blocked_dates.filter(d => d !== date)
    }));
  };

  const getCenterName = (centerId) => {
    return centers.find(c => c.id === centerId)?.name || 'Unknown Center';
  };

  const centersWithConfig = configs.map(c => c.center_id);
  const availableCenters = centers.filter(c => c.is_active && !centersWithConfig.includes(c.id));

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Appointment Configuration
              </h1>
              <p className="text-muted-foreground">
                Configure appointment availability, capacity, and booking rules
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/admin')}>
                Back to Dashboard
              </Button>
              <Button
                onClick={() => { resetForm(); setIsDialogOpen(true); }}
                disabled={availableCenters.length === 0}
                data-testid="add-config-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Configuration
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {configs.map((config) => (
              <div
                key={config.id}
                className="bg-white rounded-lg border p-6 shadow-sm"
                data-testid={`config-card-${config.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-primary">{getCenterName(config.center_id)}</h3>
                    <p className="text-sm text-muted-foreground">Appointment Configuration</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(config)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(config.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-accent" />
                    <div>
                      <p className="text-muted-foreground">Max per Day</p>
                      <p className="font-semibold">{config.max_appointments_per_day}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-accent" />
                    <div>
                      <p className="text-muted-foreground">Max per Slot</p>
                      <p className="font-semibold">{config.max_appointments_per_slot}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-accent" />
                    <div>
                      <p className="text-muted-foreground">Advance Booking</p>
                      <p className="font-semibold">{config.advance_booking_days} days</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-accent" />
                    <div>
                      <p className="text-muted-foreground">Min Notice</p>
                      <p className="font-semibold">{config.min_booking_notice_hours} hours</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Weekend Booking</span>
                    <span className={`text-sm font-medium ${config.allow_weekend_booking ? 'text-green-600' : 'text-red-600'}`}>
                      {config.allow_weekend_booking ? 'Allowed' : 'Not Allowed'}
                    </span>
                  </div>
                  
                  {config.blocked_dates && config.blocked_dates.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground mb-1">Blocked Dates:</p>
                      <div className="flex flex-wrap gap-1">
                        {config.blocked_dates.slice(0, 5).map((date) => (
                          <span key={date} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                            {date}
                          </span>
                        ))}
                        {config.blocked_dates.length > 5 && (
                          <span className="text-xs text-muted-foreground">+{config.blocked_dates.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {configs.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border">
              <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No configurations found.</p>
              <p className="text-sm text-muted-foreground">Create a configuration for each center to manage appointments.</p>
            </div>
          )}

          {availableCenters.length === 0 && configs.length > 0 && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              All active centers have been configured.
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingConfig ? 'Edit Configuration' : 'Add Configuration'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingConfig && (
              <div>
                <Label>Service Center *</Label>
                <Select
                  value={formData.center_id}
                  onValueChange={(value) => setFormData({ ...formData, center_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select center" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_per_day">Max Appointments/Day</Label>
                <Input
                  id="max_per_day"
                  type="number"
                  min="1"
                  value={formData.max_appointments_per_day}
                  onChange={(e) => setFormData({ ...formData, max_appointments_per_day: parseInt(e.target.value) })}
                />
              </div>
              
              <div>
                <Label htmlFor="max_per_slot">Max Appointments/Slot</Label>
                <Input
                  id="max_per_slot"
                  type="number"
                  min="1"
                  value={formData.max_appointments_per_slot}
                  onChange={(e) => setFormData({ ...formData, max_appointments_per_slot: parseInt(e.target.value) })}
                />
              </div>
              
              <div>
                <Label htmlFor="advance_days">Advance Booking (days)</Label>
                <Input
                  id="advance_days"
                  type="number"
                  min="1"
                  value={formData.advance_booking_days}
                  onChange={(e) => setFormData({ ...formData, advance_booking_days: parseInt(e.target.value) })}
                />
              </div>
              
              <div>
                <Label htmlFor="min_notice">Min Notice (hours)</Label>
                <Input
                  id="min_notice"
                  type="number"
                  min="0"
                  value={formData.min_booking_notice_hours}
                  onChange={(e) => setFormData({ ...formData, min_booking_notice_hours: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="weekend_booking">Allow Weekend Booking</Label>
              <Switch
                id="weekend_booking"
                checked={formData.allow_weekend_booking}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_weekend_booking: checked })}
              />
            </div>
            
            <div>
              <Label>Blocked Dates</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="date"
                  value={blockedDateInput}
                  onChange={(e) => setBlockedDateInput(e.target.value)}
                />
                <Button type="button" variant="outline" onClick={addBlockedDate}>
                  Add
                </Button>
              </div>
              
              {formData.blocked_dates.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.blocked_dates.map((date) => (
                    <span
                      key={date}
                      className="flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded"
                    >
                      {date}
                      <button
                        type="button"
                        onClick={() => removeBlockedDate(date)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-config-btn">
                {editingConfig ? 'Update' : 'Create'} Configuration
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppointmentConfig;
